import React, { useState } from 'react';
import 'react-pro-sidebar/dist/css/styles.css';
import { ProSidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from '../../../i18n';
import YourLogo from '../../Assets/Images/Pro_Logo.png';
import '../../Assets/Style/style.css';
import './sidebar.css';
import { RxHamburgerMenu } from 'react-icons/rx';
import { AiOutlineStock, AiOutlineBulb } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { getCompany } from '../../../Redux/selecters';
import { RiReservedLine, RiRefund2Line, RiStickyNoteLine, RiIndeterminateCircleFill } from 'react-icons/ri';
import { TbLockAccess } from 'react-icons/tb';
import { FcApproval } from 'react-icons/fc';
import { TbStatusChange } from 'react-icons/tb';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { FaRegFile } from 'react-icons/fa6';
import {
  MdOutlineAssignment,
  MdSupportAgent,
  MdOutlineAnalytics,
  MdOutlineManageAccounts,
  MdAutorenew,
  MdOutlineAttachMoney,
  MdOutlineSwapHoriz,
  MdOutlineAccountBalanceWallet,
  MdOutlineEventBusy,
  MdOutlineLocationCity,
  MdOutlineInventory,
  MdOutlineTimer,
} from 'react-icons/md';
import { BsGraphDownArrow, BsBuilding, BsFillPostcardFill } from 'react-icons/bs';
import { IoHomeOutline, IoMailOutline, IoSearch, IoCloseCircle } from 'react-icons/io5';
import { FaUserTie } from 'react-icons/fa';

const PMSideBar = () => {
  const { t, i18n } = useTranslation();
  const isRtl = RTL_LANGUAGES.includes(i18n.language);
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const companies = useSelector(getCompany);
  const pathname = location.pathname;
  const storedPermissions = JSON.parse(localStorage.getItem('RoleAccess')) || [];
  const accessibleForms = storedPermissions.filter((form) => form.canView);
  const [permissions, setPermission] = useState(accessibleForms);
  // formName is a fixed backend/RoleAccess literal (not the translated label) -
  // same convention as the pre-existing Dashboard check below. Rolled back from
  // gating every menu item this way: "Lease Agreement"/"Lease Renewal" (and the
  // guessed names for the other menus) don't match real RoleAccess formName
  // values, which hid everything. Only Dashboard and Lease stay gated for now.
  const canView = (formName) => permissions?.some((permission) => permission.formName === formName && permission.canView);
  const canViewLeaseAgreement = canView('Lease Agreement');
  const canViewLeaseRenewal = canView('Lease Renewal');
  const canViewUnitVacantReport = canView('Unit Vacant Report');
  const canViewCustomerCurrentBalance = canView('Customer Current Balance Report');
  // Ported from Qfinity-POS-Frontend-LeaseAggrement_k - each new screen below
  // is gated by its own formName so access can be granted independently.
  // Some of these reuse the same formName across two screens, matching the
  // actual RoleAccess data (confirmed against a real permissions export) -
  // e.g. granting 'Lease Approval' unlocks both Proposal Approval and
  // Agreement Approval, exactly like source's own convention did.
  const canViewLeaseProposal = canView('Lease Proposal') || canView('Lease Revenue');
  const canViewLeaseApproval = canView('Proposal Approval') || canView('Lease Approval');
  const canViewAgreementApproval = canView('Agreement Approval') || canView('Lease Approval');
  const canViewLeaseTermination = canView('Termination') || canView('Lease Termination');
  const canViewSingleInvoice = canView('Single Invoice');
  const canViewInstallmentInvoice = canView('Installment Invoice');
  const canViewInvoiceApproval = canView('Invoice Approval');
  const canViewLeaseGeneralInvoice = canView('Lease General Invoice');
  const canViewLeaseReceipt = canView('Lease Receipt');
  const canViewLeaseBulkReceipt = canView('Lease Bulk Receipt');
  const canViewLeaseReverseReceipt = canView('Lease Reverse Receipt');
  const canViewLeaseRevenuePosting = canView('Lease Revenue') || canView('Lease Revenue Posting');
  const canViewInventoryUpdate = canView('Inventory Update');
  const canViewAvailableUnitsReport = canView('Available Unit Report') || canView('Available Units Report') || canView('Available Unit');
  const canViewLeaseStatementReport = canView('Lease Statement Report');
  const canViewTaxStatementReport = canView('Tax Statement Report');
  const canViewTenantInvoiceReport = canView('Tenant Invoice Report');
  const canViewOccupancyReport = canView('Occupancy Report');
  const canViewLeaseOutstandingReport = canView('Lease Outstanding Report');
  const canViewMonthWiseRevenueReport = canView('Month Wise Revenue Report');
  const canViewUnitMasterListReport = canView('Unit Master List Report') || canView('Unit MasterList Report');
  const canViewRevenueAgainstInvoicesReport = canView('Revenue Against Invoices Report');
  const canViewLeaseAgingAnalysisReport = canView('Lease Aging Analysis Report');
  const canViewMoveInMoveOutReport = canView('Lease Move In Move Out Report') || canView('Move In Move Out Report');
  const canViewLeaseReceiptReport = canView('Lease Receipt Report');
  const canViewPdcChequeReport = canView('PDC List Report') || canView('Cheque List Report');
  const canViewAccruedRentReport = canView('Accrued Rent Report');
  const canViewOverstayDaysReport = canView('Overstay Days Report');
  const canViewJvImport = canView('JV Import');
  const canViewLeaseInvoiceImport = canView('Lease Invoice Import');
  const canViewLeaseReceiptInvoice = canView('Lease Receipt Import');
  const canViewPropertyImport = canView('Property Master Import');
  const canViewUnitImport = canView('Unit Master Import');
  const canViewCustomerImport = canView('Customer Master Import');
  const canViewCustomerMasterRA = canView('Customer Master RA');
  const canViewUnitMasterRA = canView('Unit Master RA');
  const canViewAnyReport = canViewAvailableUnitsReport || canViewLeaseStatementReport || canViewTaxStatementReport ||
    canViewTenantInvoiceReport || canViewOccupancyReport || canViewLeaseOutstandingReport || canViewMonthWiseRevenueReport ||
    canViewUnitMasterListReport || canViewRevenueAgainstInvoicesReport || canViewLeaseAgingAnalysisReport ||
    canViewMoveInMoveOutReport || canViewLeaseReceiptReport || canViewPdcChequeReport || canViewAccruedRentReport ||
    canViewOverstayDaysReport;
  const canViewAnyFinancePosting = canViewJvImport || canViewLeaseInvoiceImport || canViewLeaseReceiptInvoice || canViewPropertyImport || canViewUnitImport || canViewCustomerImport;

  // Menu search - filters by the rendered (translated) label text. Matching
  // is done per top-level item; for a SubMenu, matching either the group's
  // own title or any of its child labels keeps the whole group visible
  // (and forces it open) rather than filtering children individually, since
  // this sidebar's items are hardcoded JSX rather than a data-driven list.
  const [search, setSearch] = useState('');
  const searchTerm = search.trim().toLowerCase();
  const matches = (label) => !searchTerm || (label || '').toLowerCase().includes(searchTerm);
  const groupMatches = (title, childLabels) => matches(title) || childLabels.some((label) => matches(label));

  const leaseGroupOr = canViewLeaseAgreement || canViewLeaseRenewal || canViewUnitVacantReport || canViewCustomerCurrentBalance ||
    canViewLeaseProposal || canViewLeaseApproval || canViewAgreementApproval || canViewLeaseTermination;
  const leaseGroupVisible = leaseGroupOr && groupMatches(t('sidebar.lease'), [
    t('sidebar.leaseAgreement'), t('sidebar.leaseRenewal'), t('sidebar.unitVacantReport'), t('sidebar.customerCurrentBalance'),
    t('sidebar.leaseProposal'), t('sidebar.leaseApproval'), t('sidebar.agreementApproval'), t('sidebar.leaseTermination'),
  ]);

  const invoicesGroupOr = canViewSingleInvoice || canViewInstallmentInvoice || canViewInvoiceApproval || canViewLeaseGeneralInvoice;
  const invoicesGroupVisible = invoicesGroupOr && groupMatches(t('sidebar.invoices'), [
    t('sidebar.singleInvoice'), t('sidebar.installmentInvoice'), t('sidebar.invoiceApproval'), t('sidebar.leaseGeneralInvoice'), 'Invoice Listing & Email',
  ]);

  const receiptsGroupOr = canViewLeaseReceipt || canViewLeaseBulkReceipt || canViewLeaseReverseReceipt;
  const receiptsGroupVisible = receiptsGroupOr && groupMatches(t('sidebar.receipts'), [
    t('sidebar.leaseReceipt'), t('sidebar.leaseBulkReceipt'), t('sidebar.leaseReverseReceipt'),
  ]);

  const postingGroupVisible = canViewLeaseRevenuePosting && groupMatches(t('sidebar.posting'), [t('sidebar.leaseRevenuePosting')]);

  const financePostingGroupVisible = canViewAnyFinancePosting && groupMatches(t('sidebar.financePosting'), [
    t('sidebar.jvImport'), t('sidebar.leaseInvoiceImport'), t('sidebar.leaseReceiptInvoice'),
    t('sidebar.propertyImport'), t('sidebar.unitImport'), t('sidebar.customerImport'),
  ]);

  const reportsGroupVisible = canViewAnyReport && groupMatches(t('sidebar.leaseReports'), [
    t('sidebar.availableUnitsReport'), t('sidebar.leaseStatementReport'), t('sidebar.taxStatementReport'),
    t('sidebar.tenantInvoiceReport'), t('sidebar.occupancyReport'), t('sidebar.leaseOutstandingReport'),
    t('sidebar.monthWiseRevenueReport'), t('sidebar.unitMasterListReport'), t('sidebar.revenueAgainstInvoicesReport'),
    t('sidebar.leaseAgingAnalysisReport'), t('sidebar.moveInMoveOutReport'), t('sidebar.leaseReceiptReport'),
    t('sidebar.pdcChequeReport'), t('sidebar.accruedRentReport'), t('sidebar.overstayDaysReport'),
  ]);

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  return (
    <div>
      <ProSidebar collapsed={collapsed} rtl={isRtl}>
        {collapsed ? (
          // <a href="/Inventory" className="navbar-brand start">
          //   <img src={companies?.selectedCompany?.logo || YourLogo} alt="Logo" style={{ height: '40px', paddingTop: '8px', paddingBottom: '8px' }} />
          // </a>
          ''
        ) : (
          <a href="/" className="navbar-brand start">
            <img src={companies?.selectedCompany?.logo || YourLogo} alt="Logo" style={{ height: '75px' }} />
            {/* <span className="logoName" style={{ marginLeft: '8px' }}>
              {companies?.selectedCompany?.logo?.split('/').pop()?.split('.').slice(0, -1).join('.')?.charAt(0).toUpperCase() +
                companies?.selectedCompany?.logo?.split('/').pop()?.split('.').slice(0, -1).join('.')?.slice(1).toLowerCase()}
            </span> */}
          </a>
        )}
        {!collapsed && (
          <div className="sidebar-search-box">
            <IoSearch size={16} className="sidebar-search-icon" />
            <input
              type="text"
              className="sidebar-search-input"
              placeholder={t('sidebar.searchPlaceholder', 'Search menu...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IoCloseCircle size={16} className="sidebar-search-clear" onClick={() => setSearch('')} />
            )}
          </div>
        )}
        <Menu className="mt-2">
          {permissions?.some((permission) => permission.formName === 'Dashboard' && permission.canView) && matches(t('sidebar.dashboard')) && (
            <MenuItem icon={<AiOutlineStock size={23} />} active={location.pathname === '/'}>
              <NavLink to="/">
                <div>{t('sidebar.dashboard')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Property Dashboard (customer module) - a second, distinct dashboard
              page (dashboard/dashBoard.js at /listing), grouped with the rest
              of the customer module menu items. Labeled differently from the
              top-level "Dashboard" above to avoid two identically-named menu
              items pointing at two different pages. Ungated to match its
              siblings below (Customer/Property/Unit/... carry no formName gate). */}
          {matches(t('sidebar.customerDashboard')) && (
            <MenuItem icon={<AiOutlineStock size={23} />} active={pathname === '/listing'}>
              <NavLink to="/listing">
                <div>{t('sidebar.customerDashboard')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Customer */}
          {matches(t('sidebar.customerMaster')) && (
            <MenuItem icon={<MdOutlineManageAccounts size={23} />} active={pathname.startsWith('/Customer/Property/Master/Customer/')}>
              <NavLink to="/Customer/Property/Master/Customer/Listing">
                <div>{t('sidebar.customerMaster')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Customer RA - copied from the RA project's customerMaster module,
              gated by its own formName since (unlike Customer above) it's a
              newly-added screen rather than an original, always-visible one. */}
          {canViewCustomerMasterRA && matches('Customer Master RA') && (
            <MenuItem icon={<MdOutlineManageAccounts size={23} />} active={pathname.startsWith('/Customer/Property/Master/CustomerRA/')}>
              <NavLink to="/Customer/Property/Master/CustomerRA/Listing">
                <div>Customer Master RA</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Property */}
          {matches(t('sidebar.propertyMaster')) && (
            <MenuItem icon={<BsBuilding size={20} />} active={pathname.startsWith('/Customer/Property/Master/Property/')}>
              <NavLink to="/Customer/Property/Master/Property/Listing">
                <div>{t('sidebar.propertyMaster')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Unit */}
          {matches(t('sidebar.unitMaster')) && (
            <MenuItem icon={<HiOutlineOfficeBuilding size={22} />} active={pathname.startsWith('/Customer/Property/Master/Unit/')}>
              <NavLink to="/Customer/Property/Master/Unit/Listing">
                <div>{t('sidebar.unitMaster')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Unit RA - copied from the RA project's unitMaster module, gated
              the same way as Customer Master RA above. */}
          {canViewUnitMasterRA && matches('Unit Master RA') && (
            <MenuItem icon={<HiOutlineOfficeBuilding size={22} />} active={pathname.startsWith('/Customer/Property/Master/UnitRA/')}>
              <NavLink to="/Customer/Property/Master/UnitRA/Listing">
                <div>Unit Master </div>
              </NavLink>
            </MenuItem>
          )}

          {/* Community */}
          {matches(t('sidebar.communityMaster')) && (
            <MenuItem icon={<MdOutlineLocationCity size={22} />} active={pathname.startsWith('/Customer/Property/Master/Community/')}>
              <NavLink to="/Customer/Property/Master/Community/Listing">
                <div>{t('sidebar.communityMaster')}</div>
              </NavLink>
            </MenuItem>
          )}
          {/* Agent */}
          {matches(t('sidebar.agentMaster')) && (
            <MenuItem icon={<FaUserTie size={20} />} active={pathname.startsWith('/Customer/Property/Master/Agent/')}>
              <NavLink to="/Customer/Property/Master/Agent/Listing">
                <div>{t('sidebar.agentMaster')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Unit Block */}
          {matches(t('sidebar.unitBlock')) && (
            <MenuItem icon={<TbLockAccess size={22} />} active={pathname.startsWith('/Customer/Property/Master/UnitBlock/')}>
              <NavLink to="/Customer/Property/Master/UnitBlock/Listing">
                <div>{t('sidebar.unitBlock')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Unit Release */}
          {matches(t('sidebar.unitRelease')) && (
            <MenuItem icon={<RiReservedLine size={22} />} active={pathname.startsWith('/Customer/Property/Master/UnitRelease/')}>
              <NavLink to="/Customer/Property/Master/UnitRelease/Listing">
                <div>{t('sidebar.unitRelease')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Lease (ported from Qfinity-POS-Frontend-LeaseAggrement_k) - gated by
              RoleAccess the same way Dashboard is above (formName matches the
              menu label, per the same convention every other sidebar/page in
              this app uses for its own formName check). Positioned after Unit
              Release per requirements. Unit Vacant Report and Customer
              Current Balance are gated by their own formNames so they can be
              granted independently of Lease Agreement/Renewal access. */}
          {leaseGroupVisible && (
            <SubMenu icon={<BsFillPostcardFill size={22} />} title={t('sidebar.lease')} {...(searchTerm ? { open: true } : {})}>
              {canViewLeaseAgreement && (
                <MenuItem icon={<AiOutlineBulb size={18} />} active={pathname.startsWith('/Lease/Management/Lease/Agreement/')}>
                  <NavLink to="/Lease/Management/Lease/Agreement/Listing">
                    <div>{t('sidebar.leaseAgreement')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseRenewal && (
                <MenuItem icon={<MdAutorenew size={18} />} active={pathname.startsWith('/Lease/Management/Lease/Renewal/')}>
                  <NavLink to="/Lease/Management/Lease/Renewal/Listing">
                    <div>{t('sidebar.leaseRenewal')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewUnitVacantReport && (
                <MenuItem icon={<MdOutlineEventBusy size={18} />} active={pathname.startsWith('/Lease/Management/Report/UnitVacant')}>
                  <NavLink to="/Lease/Management/Report/UnitVacant">
                    <div>{t('sidebar.unitVacantReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewCustomerCurrentBalance && (
                <MenuItem icon={<MdOutlineAccountBalanceWallet size={18} />} active={pathname.startsWith('/Lease/Management/Report/CustomerCurrentBalance')}>
                  <NavLink to="/Lease/Management/Report/CustomerCurrentBalance">
                    <div>{t('sidebar.customerCurrentBalance')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseProposal && (
                <MenuItem icon={<MdOutlineAssignment size={18} />} active={pathname.startsWith('/Lease/Management/Proposal/') && !pathname.startsWith('/Lease/Management/Proposal/Approval')}>
                  <NavLink to="/Lease/Management/Proposal/Listing">
                    <div>{t('sidebar.leaseProposal')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseApproval && (
                <MenuItem icon={<FcApproval size={18} />} active={pathname.startsWith('/Lease/Management/Proposal/Approval')}>
                  <NavLink to="/Lease/Management/Proposal/Approval">
                    <div>{t('sidebar.leaseApproval')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewAgreementApproval && (
                <MenuItem icon={<TbStatusChange size={18} />} active={pathname.startsWith('/Lease/Management/Agreement/Approval')}>
                  <NavLink to="/Lease/Management/Agreement/Approval">
                    <div>{t('sidebar.agreementApproval')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseTermination && (
                <MenuItem icon={<RiIndeterminateCircleFill size={18} />} active={pathname.startsWith('/Lease/Management/Termination')}>
                  <NavLink to="/Lease/Management/Termination/Listing">
                    <div>{t('sidebar.leaseTermination')}</div>
                  </NavLink>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* Invoices (ported) */}
          {invoicesGroupVisible && (
            <SubMenu icon={<FaRegFile size={20} />} title={t('sidebar.invoices')} {...(searchTerm ? { open: true } : {})}>
              {canViewSingleInvoice && (
                <MenuItem icon={<MdOutlineAttachMoney size={18} />} active={pathname.startsWith('/Lease/Management/Invoice/Single')}>
                  <NavLink to="/Lease/Management/Invoice/Single/Listing">
                    <div>{t('sidebar.singleInvoice')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewInstallmentInvoice && (
                <MenuItem icon={<MdOutlineAnalytics size={18} />} active={pathname.startsWith('/Lease/Management/Invoice/Installment')}>
                  <NavLink to="/Lease/Management/Invoice/Installment/Listing">
                    <div>{t('sidebar.installmentInvoice')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewInvoiceApproval && (
                <MenuItem icon={<RiRefund2Line size={18} />} active={pathname.startsWith('/Lease/Management/Invoice/Approval')}>
                  <NavLink to="/Lease/Management/Invoice/Approval">
                    <div>{t('sidebar.invoiceApproval')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseGeneralInvoice && (
                <MenuItem icon={<MdSupportAgent size={18} />} active={pathname.startsWith('/Lease/Management/Invoice/General') && !pathname.startsWith('/Lease/Management/Invoice/General/Receipt')}>
                  <NavLink to="/Lease/Management/Invoice/General/Listing">
                    <div>{t('sidebar.leaseGeneralInvoice')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {(canViewInstallmentInvoice || canViewLeaseGeneralInvoice) && (
                <MenuItem icon={<IoMailOutline size={18} />} active={pathname.startsWith('/Lease/Management/Invoice/ListingEmail')}>
                  <NavLink to="/Lease/Management/Invoice/ListingEmail">
                    <div>Invoice Listing & Email</div>
                  </NavLink>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* General Invoice Receipt - its own standalone top-level menu item
              (not nested inside Invoices) per explicit request, gated by the
              same permission as Lease General Invoice since there's no
              separate backend RoleAccess form for it yet. */}
          {canViewLeaseGeneralInvoice && matches(t('sidebar.leaseGeneralInvoiceReceipt')) && (
            <MenuItem icon={<RiStickyNoteLine size={20} />} active={pathname.startsWith('/Lease/Management/Invoice/General/Receipt')}>
              <NavLink to="/Lease/Management/Invoice/General/Receipt">
                <div>{t('sidebar.leaseGeneralInvoiceReceipt')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Receipts (ported) */}
          {receiptsGroupVisible && (
            <SubMenu icon={<RiStickyNoteLine size={20} />} title={t('sidebar.receipts')} {...(searchTerm ? { open: true } : {})}>
              {canViewLeaseReceipt && (
                <MenuItem icon={<RiStickyNoteLine size={18} />} active={pathname.startsWith('/Lease/Management/Receipt/') && !pathname.startsWith('/Lease/Management/Receipt/Bulk') && !pathname.startsWith('/Lease/Management/Receipt/Reverse')}>
                  <NavLink to="/Lease/Management/Receipt/Listing">
                    <div>{t('sidebar.leaseReceipt')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseBulkReceipt && (
                <MenuItem icon={<MdSupportAgent size={18} />} active={pathname.startsWith('/Lease/Management/Receipt/Bulk')}>
                  <NavLink to="/Lease/Management/Receipt/Bulk/Listing">
                    <div>{t('sidebar.leaseBulkReceipt')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseReverseReceipt && (
                <MenuItem icon={<RiRefund2Line size={18} />} active={pathname.startsWith('/Lease/Management/Receipt/Reverse')}>
                  <NavLink to="/Lease/Management/Receipt/Reverse">
                    <div>{t('sidebar.leaseReverseReceipt')}</div>
                  </NavLink>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* Posting (ported) */}
          {postingGroupVisible && (
            <SubMenu icon={<MdOutlineSwapHoriz size={20} />} title={t('sidebar.posting')} {...(searchTerm ? { open: true } : {})}>
              <MenuItem icon={<MdOutlineSwapHoriz size={18} />} active={pathname.startsWith('/Lease/Management/Posting/Revenue')}>
                <NavLink to="/Lease/Management/Posting/Revenue">
                  <div>{t('sidebar.leaseRevenuePosting')}</div>
                </NavLink>
              </MenuItem>
            </SubMenu>
          )}

          {/* Finance Posting (ported) */}
          {financePostingGroupVisible && (
            <SubMenu icon={<MdOutlineAttachMoney size={20} />} title={t('sidebar.financePosting')} {...(searchTerm ? { open: true } : {})}>
              {canViewJvImport && (
                <MenuItem icon={<BsGraphDownArrow size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/JVImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/JVImport">
                    <div>{t('sidebar.jvImport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseInvoiceImport && (
                <MenuItem icon={<MdOutlineAnalytics size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/InvoiceImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/InvoiceImport">
                    <div>{t('sidebar.leaseInvoiceImport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseReceiptInvoice && (
                <MenuItem icon={<RiStickyNoteLine size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/ReceiptImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/ReceiptImport">
                    <div>{t('sidebar.leaseReceiptInvoice')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewPropertyImport && (
                <MenuItem icon={<BsBuilding size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/PropertyImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/PropertyImport">
                    <div>{t('sidebar.propertyImport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewUnitImport && (
                <MenuItem icon={<HiOutlineOfficeBuilding size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/UnitImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/UnitImport">
                    <div>{t('sidebar.unitImport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewCustomerImport && (
                <MenuItem icon={<MdOutlineManageAccounts size={18} />} active={pathname.startsWith('/Lease/Management/FinancePosting/CustomerImport')}>
                  <NavLink to="/Lease/Management/FinancePosting/CustomerImport">
                    <div>{t('sidebar.customerImport')}</div>
                  </NavLink>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* Inventory Update (ported) - standalone top-level item, matching source */}
          {canViewInventoryUpdate && matches(t('sidebar.inventoryUpdate')) && (
            <MenuItem icon={<MdOutlineInventory size={22} />} active={pathname.startsWith('/Lease/Management/Inventory/Update')}>
              <NavLink to="/Lease/Management/Inventory/Update">
                <div>{t('sidebar.inventoryUpdate')}</div>
              </NavLink>
            </MenuItem>
          )}

          {/* Reports (ported) - distinct from the 2 report items already inside Lease */}
          {reportsGroupVisible && (
            <SubMenu icon={<BsGraphDownArrow size={20} />} title={t('sidebar.leaseReports')} {...(searchTerm ? { open: true } : {})}>
              {canViewAvailableUnitsReport && (
                <MenuItem icon={<MdOutlineAssignment size={18} />} active={pathname.startsWith('/Lease/Management/Report/Occupancy')}>
                  <NavLink to="/Lease/Management/Report/Occupancy">
                    <div>{t('sidebar.availableUnitsReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseStatementReport && (
                <MenuItem icon={<FcApproval size={18} />} active={pathname.startsWith('/Lease/Management/Report/Lease_Statement')}>
                  <NavLink to="/Lease/Management/Report/Lease_Statement">
                    <div>{t('sidebar.leaseStatementReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewTaxStatementReport && (
                <MenuItem icon={<TbStatusChange size={18} />} active={pathname.startsWith('/Lease/Management/Report/TaxStatement')}>
                  <NavLink to="/Lease/Management/Report/TaxStatement">
                    <div>{t('sidebar.taxStatementReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewTenantInvoiceReport && (
                <MenuItem icon={<RiIndeterminateCircleFill size={18} />} active={pathname.startsWith('/Lease/Management/Report/TenantInvoice')}>
                  <NavLink to="/Lease/Management/Report/TenantInvoice">
                    <div>{t('sidebar.tenantInvoiceReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewOccupancyReport && (
                <MenuItem icon={<FaRegFile size={18} />} active={pathname.startsWith('/Lease/Management/Report/TenantOccupancy')}>
                  <NavLink to="/Lease/Management/Report/TenantOccupancy">
                    <div>{t('sidebar.occupancyReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseOutstandingReport && (
                <MenuItem icon={<MdOutlineAttachMoney size={18} />} active={pathname.startsWith('/Lease/Management/Report/LeaseOutstanding')}>
                  <NavLink to="/Lease/Management/Report/LeaseOutstanding">
                    <div>{t('sidebar.leaseOutstandingReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewMonthWiseRevenueReport && (
                <MenuItem icon={<MdOutlineAnalytics size={18} />} active={pathname.startsWith('/Lease/Management/Report/MonthWiseRevenue')}>
                  <NavLink to="/Lease/Management/Report/MonthWiseRevenue">
                    <div>{t('sidebar.monthWiseRevenueReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewUnitMasterListReport && (
                <MenuItem icon={<RiRefund2Line size={18} />} active={pathname.startsWith('/Lease/Management/Report/UnitMasterList')}>
                  <NavLink to="/Lease/Management/Report/UnitMasterList">
                    <div>{t('sidebar.unitMasterListReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewRevenueAgainstInvoicesReport && (
                <MenuItem icon={<MdSupportAgent size={18} />} active={pathname.startsWith('/Lease/Management/Report/RevenueAgainstInvoices')}>
                  <NavLink to="/Lease/Management/Report/RevenueAgainstInvoices">
                    <div>{t('sidebar.revenueAgainstInvoicesReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseAgingAnalysisReport && (
                <MenuItem icon={<RiStickyNoteLine size={18} />} active={pathname.startsWith('/Lease/Management/Report/AgingAnalysis')}>
                  <NavLink to="/Lease/Management/Report/AgingAnalysis">
                    <div>{t('sidebar.leaseAgingAnalysisReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewMoveInMoveOutReport && (
                <MenuItem icon={<MdOutlineSwapHoriz size={18} />} active={pathname.startsWith('/Lease/Management/Report/Move_IN_OUT')}>
                  <NavLink to="/Lease/Management/Report/Move_IN_OUT">
                    <div>{t('sidebar.moveInMoveOutReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewLeaseReceiptReport && (
                <MenuItem icon={<IoHomeOutline size={18} />} active={pathname.startsWith('/Lease/Management/Report/LeaseReceipts')}>
                  <NavLink to="/Lease/Management/Report/LeaseReceipts">
                    <div>{t('sidebar.leaseReceiptReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewPdcChequeReport && (
                <MenuItem icon={<BsGraphDownArrow size={18} />} active={pathname.startsWith('/Lease/Management/Report/PDCList')}>
                  <NavLink to="/Lease/Management/Report/PDCList">
                    <div>{t('sidebar.pdcChequeReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewAccruedRentReport && (
                <MenuItem icon={<MdOutlineAssignment size={18} />} active={pathname.startsWith('/Lease/Management/Report/AccruedRent')}>
                  <NavLink to="/Lease/Management/Report/AccruedRent">
                    <div>{t('sidebar.accruedRentReport')}</div>
                  </NavLink>
                </MenuItem>
              )}

              {canViewOverstayDaysReport && (
                <MenuItem icon={<MdOutlineTimer size={18} />} active={pathname.startsWith('/Lease/Management/Report/OverstayDays')}>
                  <NavLink to="/Lease/Management/Report/OverstayDays">
                    <div>{t('sidebar.overstayDaysReport')}</div>
                  </NavLink>
                </MenuItem>
              )}
            </SubMenu>
          )}

          {/* Installment Invoice Email - hidden from the menu for now, route stays registered */}
          {/* <MenuItem icon={<IoMailOutline size={22} />} active={pathname.startsWith('/Customer/Property/Master/InstallmentInvoiceEmail/')}>
            <NavLink to="/Customer/Property/Master/InstallmentInvoiceEmail/Listing">
              <div>{t('sidebar.installmentInvoiceEmail')}</div>
            </NavLink>
          </MenuItem> */}
        </Menu>
        <div onClick={handleToggle} className="sidebar-toggle-btn" style={{ insetInlineStart: collapsed ? '100px' : '300px' }}>
          <RxHamburgerMenu size={25} />
        </div>
      </ProSidebar>
    </div>
  );
};

export default PMSideBar;
